/**
 * AuthPromptModal container
 * Render verification message to users who is calling to PayPal customer service and be authorized through HCR Home Step Up follow
 */
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import Modal from 'kepler/lib/components/Modal';
import ModalBody from 'kepler/lib/components/ModalBody';
import Button from 'kepler/lib/components/Button';
import Spinner from 'kepler/lib/components/Spinner';

import l10n from '../../lib/reactPPl10nHelper';
import Page from '../../lib/page';
import { trackLink, trackPage } from '../../lib/analytics-helper';
import { authPositiveConfirm, authNegativeConfirm, closeWindow } from '../actions/actions-auth-prompt';
import { urlConstants } from '../../common/commonConstants';

const label = {
  noticeHeader: l10n.getContent('smarthelp/onCallStepUp.onCallStepUp.verify.message1'),
  noticeLabel: l10n.getContent('smarthelp/onCallStepUp.onCallStepUp.verify.message2'),
  yes: l10n.getContent('smarthelp/onCallStepUp.onCallStepUp.yes'),
  no: l10n.getContent('smarthelp/onCallStepUp.onCallStepUp.no'),
  failure: l10n.getContent('smarthelp/onCallStepUp.onCallStepUp.failure'),
  thanks: l10n.getContent('smarthelp/onCallStepUp.onCallStepUp.thanks'),
  notOnPhone: l10n.getContent('smarthelp/onCallStepUp.onCallStepUp.newNotOnPhone', {
    contactUsUrl: urlConstants.CONTACT_US_URL,
  }),
  close: l10n.getContent('smarthelp/onCallStepUp.onCallStepUp.close'),
};

class AuthPromptModal extends Component {
  static getModalType() {
    return Page.isMobileLayout() ? 'default' : 'dialog';
  }

  constructor(props) {
    super(props);

    this.clickHandlerPositive = this.clickHandlerPositive.bind(this);
    this.clickHandlerNegative = this.clickHandlerNegative.bind(this);
    this.clickHandlerClose = this.clickHandlerClose.bind(this);
  }

  componentDidMount() {
    if (this.props.display) {
      trackPage({ id: 'confirmAuthPrompt' }); // FPTI impression event, when display confirm prompt
    }
  }

  componentDidUpdate(prevProps) {
    if (!prevProps.error && this.props.error) {
      trackPage({ id: 'confirmAuthFailure' }); // FPTI impression event for auth failure case
    }
  }

  getConfirmationNotice() {
    const confirmationNotice = this.props.authorized ? label.thanks : label.notOnPhone;
    return Page.getHTMLElement({ message: confirmationNotice, useHTML: true });
  }

  // 'Yes' button click handler
  clickHandlerPositive() {
    this.props.authPositiveConfirm();
    trackLink({ id: 'confirmAuthPromptOnClick', link: 'comfirm-auth-prompt|click|yes' });
  }

  // 'No' button click handler
  clickHandlerNegative() {
    this.props.authNegativeConfirm();
    trackLink({ id: 'confirmAuthPromptOnClick', link: 'comfirm-auth-prompt|click|no' });
  }

  // 'Close' modal click handler
  clickHandlerClose() {
    this.props.closeWindow();
    trackLink({ id: 'confirmAuthPromptOnClick', link: 'comfirm-auth-prompt|click|close' });
  }

  // Render error message if the auth service failed
  renderAuthFailed() {
    return (
      <div className="auth-prompt-confirmation-container auth-prompt-failure">
        <h2 className="auth-prompt-failure-header">
          <div className="auth-prompt-failure-message">{label.failure}</div>
          <div className="auth-prompt-failure-message">
            {Page.getHTMLElement({ message: label.notOnPhone, useHTML: true })}
          </div>
        </h2>
        <Button className="auth-prompt-confirmation-btn" onClick={this.clickHandlerClose} type="secondary">
          {label.close}
        </Button>
      </div>
    );
  }

  // Render 'Thank you' or addition help message
  renderConfirmation() {
    return (
      <div className="auth-prompt-confirmation-container">
        <h2 className="auth-prompt-confirmation-header">{this.getConfirmationNotice()}</h2>
        <Button className="auth-prompt-confirmation-btn" onClick={this.clickHandlerClose} type="secondary">
          {label.close}
        </Button>
      </div>
    );
  }

  // Render verification UI with YES/NO button to confirm and verify
  renderVerificationInfo() {
    return (
      <div className="auth-prompt-verify-container">
        <h2 className="auth-prompt-verify-header">{label.noticeHeader}</h2>
        <div className="auth-prompt-verify-label">{label.noticeLabel}</div>
        <div className="auth-prompt-verify-button-set">
          <Button className="auth-prompt-verify-btn" onClick={this.clickHandlerPositive}>
            {label.yes}
          </Button>
          <Button className="auth-prompt-verify-btn" onClick={this.clickHandlerNegative} type="secondary">
            {label.no}
          </Button>
        </div>
      </div>
    );
  }

  renderModalBody() {
    if (this.props.isSending) {
      // Render Spinner after selecting 'Yes/No' and waiting for auth request.
      return <Spinner size="lg" type="primary" />;
    }

    if (this.props.error) {
      // Render error message, when auth request is failed.
      return this.renderAuthFailed();
    }

    if (!this.props.decided) {
      return this.renderVerificationInfo();
    }

    return this.renderConfirmation();
  }

  render() {
    return (
      <div>
        <Modal
          isOpen={this.props.display}
          type={AuthPromptModal.getModalType()}
          width="medium"
          onCancel={this.clickHandlerClose}
          backdropClosesModal={false}
          showCloseButton={this.props.decided}
        >
          <ModalBody>
            <div className="auth-prompt-modal-body">{this.renderModalBody()}</div>
          </ModalBody>
        </Modal>
      </div>
    );
  }
}

AuthPromptModal.propTypes = {
  display: PropTypes.bool,
  decided: PropTypes.bool,
  authorized: PropTypes.bool,
  authPositiveConfirm: PropTypes.func.isRequired,
  authNegativeConfirm: PropTypes.func.isRequired,
  closeWindow: PropTypes.func.isRequired,
  isSending: PropTypes.bool,
  error: PropTypes.bool,
};

AuthPromptModal.defaultProps = {
  display: false,
  decided: false,
  authorized: false,
  isSending: false,
  error: false,
};

function mapStateToProps(state) {
  return {
    display: state.authPrompt.display,
    decided: state.authPrompt.decided,
    authorized: state.authPrompt.authorized,
    isSending: state.authPrompt.isSending,
    error: state.authPrompt.error,
  };
}

function mapDispatchToProps(dispatch) {
  return bindActionCreators({ authPositiveConfirm, authNegativeConfirm, closeWindow }, dispatch);
}

export default connect(mapStateToProps, mapDispatchToProps)(AuthPromptModal);
